import { motion } from 'framer-motion';
import { ShopifyBadge, MicrosoftBadge } from './PartnerBadges';

const highlights = [
  'Custom software tailored to your needs',
  'AI-powered intelligent solutions',
  'Enterprise-grade security standards',
  'Scalable cloud architectures',
  'Dedicated support team',
  'Agile development methodology',
];

export const AboutSection = () => {
  return (
    <section id="about" className="section-padding bg-[#F6F9FC]" data-testid="about-section">
      <div className="container-custom">
        <div className="grid lg:grid-cols-2 gap-20 items-center">

          {/* Image */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}
            className="relative"
          >
            {/* Corner bracket — top-left */}
            <div className="absolute -top-3 -left-3 z-10">
              <div className="w-12 h-px bg-[#2563EB]/30" />
              <div className="w-px h-12 bg-[#2563EB]/30" />
            </div>
            {/* Corner bracket — bottom-right */}
            <div className="absolute -bottom-3 -right-3 z-10 flex flex-col items-end">
              <div className="w-px h-12 bg-[#2563EB]/30 ml-auto" />
              <div className="w-12 h-px bg-[#2563EB]/30" />
            </div>

            <div className="relative overflow-hidden">
              <img
                src="https://images.pexels.com/photos/8068836/pexels-photo-8068836.jpeg?auto=compress&cs=tinysrgb&w=600"
                alt="Smart Solution Team"
                className="w-full h-[500px] object-cover"
                style={{ filter: 'saturate(0.8) brightness(0.95)' }}
                data-testid="about-image"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#F6F9FC]/30 via-transparent to-transparent" />
              <div className="absolute inset-0 border border-[#E2E8F0]" />
            </div>

            {/* Floating stats */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.4 }}
              className="absolute -bottom-6 -right-6 bg-white border border-[#E2E8F0] p-6 z-20 shadow-lg"
            >
              <div className="flex items-center gap-6">
                <div className="text-center">
                  <div className="font-display text-3xl font-bold text-[#2563EB]">5+</div>
                  <div className="font-mono text-[0.58rem] tracking-widest text-[#374151] mt-1">YEARS</div>
                </div>
                <div className="w-px h-10 bg-[#E2E8F0]" />
                <div className="text-center">
                  <div className="font-display text-3xl font-bold text-[#0F172A]">100+</div>
                  <div className="font-mono text-[0.58rem] tracking-widest text-[#374151] mt-1">PROJECTS</div>
                </div>
              </div>
            </motion.div>
          </motion.div>

          {/* Content */}
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7, delay: 0.15 }}
          >
            <div className="flex items-center gap-3 mb-6">
              <span className="section-label">About Us</span>
              <span className="w-6 h-px bg-[#2563EB]/30" />
            </div>
            <h2
              className="font-display font-bold text-[#0F172A] mb-6"
              style={{ fontSize: 'clamp(2rem, 4vw, 3.5rem)' }}
            >
              Your Technology Partner<br />
              for <span className="italic text-gold">Digital Success</span>
            </h2>
            <p className="text-[#374151] leading-relaxed mb-10">
              Smart Solution is a technology consulting and development company delivering
              custom software, AI solutions, and enterprise-grade platforms tailored to
              business needs. We bridge the gap between complex technology and practical
              business outcomes.
            </p>

            {/* Highlights */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-y-3 gap-x-6 mb-10">
              {highlights.map((item, i) => (
                <motion.div
                  key={item}
                  initial={{ opacity: 0, x: 10 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: 0.1 + i * 0.07 }}
                  className="flex items-center gap-3"
                >
                  <div className="w-1.5 h-1.5 rounded-full bg-[#2563EB] shrink-0" />
                  <span className="text-[#374151] text-sm">{item}</span>
                </motion.div>
              ))}
            </div>

            {/* Partner certifications */}
            <div className="flex flex-wrap gap-3 mb-10">
              <ShopifyBadge />
              <MicrosoftBadge />
            </div>

            {/* Mission pull-quote */}
            <div className="border-l-2 border-[#2563EB]/30 pl-6 py-1">
              <p className="font-display italic text-[#0F172A] text-lg leading-relaxed">
                "Our mission is to empower businesses with intelligent technology solutions
                that drive growth, efficiency, and competitive advantage."
              </p>
            </div>
          </motion.div>

        </div>
      </div>
    </section>
  );
};
